/**
 * Ops modal — add a missing Shared Load corridor rate without leaving Quote Prep.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  fetchGroupRows,
  saveGroupRows,
} from "@/features/pricing/services/rateCardsService.js";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object|null} props.suggested
 * @param {() => void|Promise<void>} [props.onSaved]
 */
export default function AddCorridorRateModal({
  open = false,
  onClose = null,
  suggested = null,
  onSaved = null,
}) {
  const [originRegion, setOriginRegion] = useState("GP");
  const [destinationRegion, setDestinationRegion] = useState("WC");
  const [ratePerM3, setRatePerM3] = useState("");
  const [minimumAmount, setMinimumAmount] = useState("");
  const [season, setSeason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setOriginRegion(
      String(suggested?.originRegion || suggested?.originPricingRegion || "GP")
        .trim()
        .toUpperCase() || "GP"
    );
    setDestinationRegion(
      String(
        suggested?.destinationRegion ||
          suggested?.destinationPricingRegion ||
          "WC"
      )
        .trim()
        .toUpperCase() || "WC"
    );
    setRatePerM3("");
    setMinimumAmount("");
    setSeason(String(suggested?.season || "").trim());
  }, [open, suggested]);

  const corridorId =
    originRegion && destinationRegion
      ? `${originRegion}-${destinationRegion}`
      : suggested?.id || "";

  const handleSave = async () => {
    const rate = Number(ratePerM3);
    if (!originRegion || !destinationRegion) {
      setError("Origin and destination regions are required.");
      return;
    }
    if (!Number.isFinite(rate) || rate <= 0) {
      setError("Enter a positive rate per m³ (R).");
      return;
    }
    const min = Number(minimumAmount);
    setSaving(true);
    setError(null);
    try {
      const data = await fetchGroupRows("shared_load");
      const existing = Array.isArray(data?.rows) ? data.rows : [];
      const id = corridorId;
      const withoutDup = existing.filter((row) => {
        const sameId = String(row.id || "").toUpperCase() === id.toUpperCase();
        const samePair =
          String(row.originRegion || "").toUpperCase() === originRegion &&
          String(row.destinationRegion || "").toUpperCase() ===
            destinationRegion &&
          String(row.serviceType || "shared_load") === "shared_load" &&
          String(row.season || "") === String(season || "");
        return !(sameId || samePair);
      });
      const nextRow = {
        id,
        originRegion,
        destinationRegion,
        serviceType: "shared_load",
        season: season || "",
        direction: "",
        ratePerM3: rate,
        minimumAmount: Number.isFinite(min) && min > 0 ? min : 0,
        enabled: true,
      };
      await saveGroupRows("shared_load", {
        rows: [...withoutDup, nextRow],
      });
      if (typeof onSaved === "function") {
        await onSaved(nextRow);
      }
      if (typeof onClose === "function") onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to save corridor rate"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      data-testid="add-corridor-rate-modal"
    >
      <DialogTitle>Add corridor rate</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Creates a Shared Load rate card for{" "}
            <strong>{corridorId || "this corridor"}</strong>. After save,
            recalculate the suggested price on this quote.
          </Typography>
          {error ? (
            <Alert severity="error" data-testid="add-corridor-rate-error">
              {error}
            </Alert>
          ) : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Origin region"
              value={originRegion}
              onChange={(e) =>
                setOriginRegion(e.target.value.trim().toUpperCase())
              }
              fullWidth
              required
              inputProps={{ "data-testid": "add-corridor-origin" }}
            />
            <TextField
              label="Destination region"
              value={destinationRegion}
              onChange={(e) =>
                setDestinationRegion(e.target.value.trim().toUpperCase())
              }
              fullWidth
              required
              inputProps={{ "data-testid": "add-corridor-destination" }}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Rate per m³ (R)"
              type="number"
              value={ratePerM3}
              onChange={(e) => setRatePerM3(e.target.value)}
              fullWidth
              required
              inputProps={{
                min: 0,
                step: "0.01",
                "data-testid": "add-corridor-rate-per-m3",
              }}
            />
            <TextField
              label="Minimum (R)"
              type="number"
              value={minimumAmount}
              onChange={(e) => setMinimumAmount(e.target.value)}
              fullWidth
              inputProps={{
                min: 0,
                step: "1",
                "data-testid": "add-corridor-minimum",
              }}
            />
          </Stack>
          <FormControl fullWidth>
            <InputLabel id="add-corridor-season-label">Season (optional)</InputLabel>
            <Select
              labelId="add-corridor-season-label"
              label="Season (optional)"
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              data-testid="add-corridor-season"
            >
              <MenuItem value="">Any / blank</MenuItem>
              <MenuItem value="peak">Peak</MenuItem>
              <MenuItem value="off_peak">Off-peak</MenuItem>
              <MenuItem value="mid_month">Mid-month</MenuItem>
              <MenuItem value="month_end">Month-end</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          data-testid="add-corridor-rate-save"
        >
          {saving ? "Saving…" : "Save corridor & continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
